/** Pure helpers for the secrets screen: drafts, grant matching, confirm copy and translated API errors */

import { ApiError } from '../../lib/api-client'
import type { TranslateFn, TranslationKey } from '../../i18n'
import type {
  SecretConfirmAction,
  SecretDraft,
  SecretEntry,
  SecretGrant,
  SecretGrantDraft,
  SecretGrantPermission,
  SecretGrantTargetType,
  SecretKind,
} from './secret-dashboard-types'

export const SECRET_GRANT_PERMISSIONS: SecretGrantPermission[] = [
  'describe',
  'read',
  'reveal',
  'inject',
  'write',
  'grant',
]

export const SECRET_GRANT_TARGET_TYPES: SecretGrantTargetType[] = ['api_key', 'user', 'agent', 'skill']

export const SECRET_KINDS: SecretKind[] = ['secret', 'value']

/** Audit reasons sent when the user leaves the optional reason empty (stored data, not UI copy) */
export const DEFAULT_SECRET_REASONS = {
  create: 'web create',
  update: 'web update',
  grant: 'web grant',
  import: 'web import',
} as const

export function reasonOrDefault(reason: string, fallback: string): string {
  return reason.trim() || fallback
}

export function createEmptySecretDraft(): SecretDraft {
  return { key: '', value: '', kind: 'secret', description: '', reason: '' }
}

export function createGrantDraft(entry?: SecretEntry): SecretGrantDraft {
  return {
    targetType: 'api_key',
    targetId: '',
    targetDisplayName: '',
    keyPattern: entry?.key ?? '*',
    permissions: ['reveal'],
    ttlDays: '',
    reason: '',
  }
}

export function normalizeSecretPath(path: string): string {
  const trimmed = path.trim()
  if (!trimmed) return '/'
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

export function togglePermission(
  current: SecretGrantPermission[],
  permission: SecretGrantPermission,
): SecretGrantPermission[] {
  return current.includes(permission)
    ? current.filter((item) => item !== permission)
    : [...current, permission]
}

export function ttlDaysToEpochMs(ttlDays: string, now = Date.now()): number | null {
  const trimmed = ttlDays.trim()
  if (!trimmed) return null
  const days = Number(trimmed)
  if (!Number.isFinite(days) || days <= 0) return null
  return now + Math.round(days * 86_400_000)
}

/** Localised date + time; "Never" for empty values, "Unknown" for unparseable ones */
export function formatSecretTimestamp(value: number | string | null | undefined, t: TranslateFn, locale: string): string {
  if (value === null || value === undefined || value === '') return t('secrets.time.never')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return t('secrets.time.unknown')
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

export function keyMatchesPattern(pattern: string, key: string): boolean {
  if (pattern === '*' || pattern === key) return true
  if (pattern.endsWith('*')) return key.startsWith(pattern.slice(0, -1))
  return false
}

function pathMatches(grantPath: string, entryPath: string): boolean {
  const grant = normalizeSecretPath(grantPath)
  const entry = normalizeSecretPath(entryPath)
  if (grant === '/' || grant === entry) return true
  return entry.startsWith(`${grant.replace(/\/$/, '')}/`)
}

function isGrantExpired(expiresAt: number | null): boolean {
  return expiresAt !== null && expiresAt <= Date.now()
}

export function grantTargetsEntry(grant: SecretGrant, entry: SecretEntry): boolean {
  if (grant.revokedAt) return false
  if (isGrantExpired(grant.expiresAt)) return false
  if (grant.environment !== entry.environment) return false
  if (!pathMatches(grant.path, entry.path)) return false
  return keyMatchesPattern(grant.keyPattern, entry.key)
}

export function grantsForEntry(grants: SecretGrant[], entry: SecretEntry): SecretGrant[] {
  return grants.filter((grant) => grantTargetsEntry(grant, entry))
}

export function permissionLabel(permissions: SecretGrantPermission[], t: TranslateFn): string {
  return permissions.length > 0 ? permissions.join(', ') : t('secrets.grants.noPermissions')
}

const TARGET_TYPE_KEYS: Record<SecretGrantTargetType, TranslationKey> = {
  api_key: 'secrets.grants.target.apiKey',
  user: 'secrets.grants.target.user',
  agent: 'secrets.grants.target.agent',
  skill: 'secrets.grants.target.skill',
}

export function targetTypeLabel(type: SecretGrantTargetType, t: TranslateFn): string {
  return t(TARGET_TYPE_KEYS[type])
}

export function kindLabel(kind: SecretKind, t: TranslateFn): string {
  return kind === 'value' ? t('secrets.kind.value') : t('secrets.kind.secret')
}

export function confirmTitle(action: SecretConfirmAction, t: TranslateFn): string {
  if (action.type === 'disable') return t('secrets.confirm.disableTitle', { key: action.entry.key })
  if (action.type === 'destroy') return t('secrets.confirm.destroyTitle', { key: action.entry.key })
  if (action.type === 'revoke-grant') return t('secrets.confirm.revokeTitle', { name: action.grant.targetDisplayName })
  return t('secrets.confirm.rollbackTitle', { version: action.version.version })
}

export function confirmDescription(action: SecretConfirmAction, t: TranslateFn): string {
  if (action.type === 'destroy') return t('secrets.confirm.destroyBody')
  if (action.type === 'disable') return t('secrets.confirm.disableBody')
  if (action.type === 'revoke-grant') return t('secrets.confirm.revokeBody')
  return t('secrets.confirm.rollbackBody')
}

export function confirmLabel(action: SecretConfirmAction, t: TranslateFn): string {
  if (action.type === 'destroy') return t('secrets.confirm.destroy')
  if (action.type === 'disable') return t('secrets.confirm.disable')
  if (action.type === 'revoke-grant') return t('secrets.confirm.revoke')
  return t('secrets.confirm.rollback')
}

export function actionSuccessText(action: SecretConfirmAction, t: TranslateFn): string {
  if (action.type === 'disable') return t('secrets.toast.disabled')
  if (action.type === 'destroy') return t('secrets.toast.destroyed')
  if (action.type === 'revoke-grant') return t('secrets.toast.grantRevoked')
  return t('secrets.toast.rolledBack', { version: action.version.version })
}

export function isDangerAction(action: SecretConfirmAction | null): boolean {
  return action?.type === 'destroy' || action?.type === 'disable' || action?.type === 'revoke-grant'
}

/** Mirrors the server rule: self-grant reasons need this many non-space characters. */
export const SELF_GRANT_MIN_REASON_CHARS = 10

/** A self-grant targets the signed-in user or an API key they created. */
export function isSelfGrantTarget(
  draft: Pick<SecretGrantDraft, 'targetType' | 'targetId'>,
  currentUserId: string | null | undefined,
  ownApiKeyIds: readonly string[],
): boolean {
  const targetId = draft.targetId.trim()
  if (!targetId || !currentUserId) return false
  if (draft.targetType === 'user') return targetId === currentUserId
  if (draft.targetType === 'api_key') return ownApiKeyIds.includes(targetId)
  return false
}

export function hasSelfGrantReason(reason: string): boolean {
  return reason.replace(/\s/g, '').length >= SELF_GRANT_MIN_REASON_CHARS
}

/** Machine-readable `code` from a secrets API error body, if any */
export function secretErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined
  const data = err.data
  if (typeof data === 'object' && data !== null && 'code' in data) return String((data as { code: unknown }).code)
  return undefined
}

const ERROR_CODE_KEYS: Record<string, TranslationKey> = {
  SECRET_RATE_LIMITED: 'secrets.error.rateLimited',
  SECRET_PERMISSION_DENIED: 'secrets.error.forbidden',
  SECRET_GRANT_DENIED: 'secrets.error.grantRequired',
  SECRET_REVEAL_APPROVAL_REQUIRES_USER: 'secrets.error.forbidden',
  SECRET_AGENT_FLAG_REQUIRED: 'secrets.error.agentFlagRequired',
  SECRET_SELF_GRANT_DENIED: 'secrets.error.selfGrantDenied',
  SECRET_SELF_GRANT_REASON_REQUIRED: 'secrets.error.selfGrantReason',
  SECRET_ALREADY_EXISTS: 'secrets.error.alreadyExists',
  SECRET_VERSION_CONFLICT: 'secrets.error.versionConflict',
  SECRET_NOT_FOUND: 'secrets.error.notFound',
  SECRET_KEY_NOT_FOUND: 'secrets.error.encryptionUnavailable',
  SECRET_VERSION_NOT_FOUND: 'secrets.error.versionNotFound',
  SECRET_REASON_REQUIRED: 'secrets.error.reasonRequired',
  SECRET_REVEAL_REQUEST_NOT_FOUND: 'secrets.error.requestClosed',
  SECRET_REVEAL_REQUEST_NOT_PENDING: 'secrets.error.requestClosed',
  SECRET_REVEAL_REQUEST_EXPIRED: 'secrets.error.requestClosed',
  SECRET_REVEAL_REQUEST_CONSUMED: 'secrets.error.requestClosed',
  SECRET_REVEAL_REQUEST_DENIED: 'secrets.error.requestClosed',
  SECRET_ENCRYPTION_KEY_INVALID: 'secrets.error.encryptionUnavailable',
  SECRET_MASTER_KEY_MISSING: 'secrets.error.encryptionUnavailable',
}

/**
 * Translated, user-facing text for a failed secrets request. Known error codes map to guidance;
 * otherwise the HTTP status decides. Raw server bodies are never shown, except the field detail of
 * a validation error (the server phrases it as "field: problem").
 */
export function secretErrorMessage(err: unknown, t: TranslateFn): string {
  const code = secretErrorCode(err)
  if (code === 'SECRET_SELF_GRANT_REASON_REQUIRED') {
    return t('secrets.error.selfGrantReason', { count: SELF_GRANT_MIN_REASON_CHARS })
  }
  if (code === 'SECRET_VALIDATION_FAILED' && err instanceof ApiError) {
    return t('secrets.error.validation', { detail: err.message })
  }
  const key = code ? ERROR_CODE_KEYS[code] : undefined
  if (key) return t(key)
  if (err instanceof ApiError) {
    // Without SECRET_RATE_LIMITED this is the general API limit, not the secret access limit
    if (err.status === 429) return t('secrets.error.tooManyRequests')
    if (err.status === 401) return t('secrets.error.signedOut')
    if (err.status === 403) return t('secrets.error.forbidden')
    if (err.status === 404) return t('secrets.error.notFound')
    return t('secrets.error.server', { status: err.status })
  }
  return t('secrets.error.network')
}
